import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  Card,
  CardContent,
  Avatar,
  Divider,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  Language,
  Business,
  People,
  Public,
  ContactSupport,
} from '@mui/icons-material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useNavigate } from 'react-router-dom';
import { useGetCompaniesQuery } from '../../../../services/orgApi';
import { adminMenuItems } from '../../../../config/layout';

/**
 * MyOrganization Component
 * Displays organization information in a beautiful, modern layout
 * Based on the design shown in the reference image
 */
const MyOrganization = () => {
  const navigate = useNavigate();
  const { data: companies, isLoading, isError, error } = useGetCompaniesQuery();

  // Organization data state
  const [orgData, setOrgData] = useState(null);

  // Get breadcrumbs from layout configuration
  const currentBreadcrumbs = useMemo(() => {
    const orgManagementItem = adminMenuItems.find((item) => item.id === 'org-management');
    const myOrgItem = orgManagementItem?.subItems?.find((item) => item.id === 'my-org');
    return myOrgItem?.breadcrumbs || [];
  }, []);

  // Update orgData when companies are fetched
  useEffect(() => {
    if (companies && companies.length > 0) {
      const company = companies[0]; // Assuming first company is the main one
      const operationalInfo = company.operationalInfo || {};
      const legalInfo = company.legalInfo || {};
      const socialMedia = company.socialMedia || {};

      setOrgData({
        companyName: company.name || '',
        tagline: operationalInfo.tagline || '',
        logo: company.logo || null,
        description: operationalInfo.description || '',
        address: company.address || '',
        registrationNumber: legalInfo.registrationNumber || '',
        cinNumber: legalInfo.cinNumber || '',
        panNumber: legalInfo.panNumber || '',
        gstNumber: legalInfo.gstNumber || '',
        legalStructure: legalInfo.legalStructure || '',
        foundedYear: operationalInfo.foundedYear || '',
        revenue: operationalInfo.revenue || '',
        businessHours: operationalInfo.businessHours || '',
        timeZone: operationalInfo.timeZone || '',
        primaryLanguage: operationalInfo.primaryLanguage || '',
        currency: operationalInfo.currency || '',
        email: company.email || '',
        website: company.website || '',
        phone: company.phone || '',
        id: company.id,
        entitiesCount: company.entities?.length || 0,
        industryType: company.industryType || ''
      });
    }
  }, [companies]);

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ p: 4, textAlign: 'center' }}>
        <Typography>Loading organization details...</Typography>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container maxWidth="xl" sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">Error loading organization: {error?.data?.message || 'Unknown error'}</Typography>
        <Button onClick={() => window.location.reload()} sx={{ mt: 2 }}>Retry</Button>
      </Container>
    );
  }

  if (!orgData && !isLoading) {
    return (
      <Container maxWidth="xl" sx={{ p: 4, textAlign: 'center' }}>
        <Typography>No organization data found.</Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/org-management/org-setup')}
          sx={{ mt: 2, backgroundColor: '#7522D1' }}
        >
          Setup Organization
        </Button>
      </Container>
    );
  }

  // Optimized array-based data structure for metrics
  const companyMetrics = [
    {
      id: 'industry',
      label: 'Industry',
      value: orgData.industryType || 'Information Technology',
      icon: Business,
    },
    {
      id: 'company-size',
      label: 'Entities',
      value: `${orgData.entitiesCount} Registered Entities`,
      icon: Public,
    },
    {
      id: 'founded',
      label: 'Founded',
      value: orgData.foundedYear || 'N/A',
      icon: People,
    },
  ];

  // Optimized array-based data structure for contact information
  const contactInfo = [
    {
      id: 'email',
      label: 'General Inquiries',
      value: orgData.email || 'N/A',
      icon: Email,
    },
    {
      id: 'website',
      label: 'Official Website',
      value: orgData.website || 'N/A',
      icon: Language,
    },
    {
      id: 'phone',
      label: 'Phone Support',
      value: orgData.phone || 'N/A',
      icon: Phone,
    },
    {
      id: 'registration',
      label: 'Registration Details',
      value: `Reg: ${orgData.registrationNumber || 'N/A'}`,
      icon: Business,
      isDivider: true,
    },
    {
      id: 'tax-details',
      label: 'Tax & Legal',
      value: `PAN: ${orgData.panNumber || 'N/A'} | GST: ${orgData.gstNumber || 'N/A'}`,
      icon: Business,
    }
  ];

  const handleContactHR = () => {
    navigate('/hr/contact');
  };

  const handleEditOrganization = () => {
    navigate('/org-management/org-setup');
  };

  return (
    <Container maxWidth="xl" sx={{ m: 0, p: 3 }}>
      {/* Breadcrumb Navigation */}
      <Breadcrumbs
        separator={<NavigateNextIcon sx={{ fontSize: '1rem' }} />}
        sx={{
          marginBottom: 3,
          '& .MuiBreadcrumbs-li': {
            fontSize: '0.875rem',
          },
        }}
      >
        {currentBreadcrumbs.map((crumb, index) => (
          <Link
            key={crumb.path}
            underline={index === currentBreadcrumbs.length - 1 ? 'none' : 'hover'}
            color="inherit"
            sx={{
              cursor: index === currentBreadcrumbs.length - 1 ? 'default' : 'pointer',
              color: index === currentBreadcrumbs.length - 1 ? '#7522D1' : '#6B7280',
              fontWeight: index === currentBreadcrumbs.length - 1 ? 700 : 500,
              transition: 'all 0.2s',
              '&:hover': {
                color: '#7522D1',
              },
            }}
            onClick={() => index < currentBreadcrumbs.length - 1 && navigate(crumb.path)}
          >
            {crumb.label}
          </Link>
        ))}
      </Breadcrumbs>

      {/* Page Header */}
      <Box sx={{ marginBottom: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: '#030007',
            marginBottom: 1,
            letterSpacing: '-0.02em',
          }}
        >
          My Organization
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#6B7280',
            fontSize: '0.95rem',
            lineHeight: 1.6,
            fontWeight: 500,
          }}
        >
          View and manage your organization's profile information.
        </Typography>
      </Box>

      {/* Company Header Card */}
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          background: '#FFF',
          border: '1px solid rgba(117, 34, 209, 0.08)',
          boxShadow: '0 8px 32px rgba(117, 34, 209, 0.03)',
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '16px',
        }}
      >
        {/* Cover Banner */}
        <Box
          sx={{
            height: '160px',
            background: 'linear-gradient(135deg, #7522D1 0%, #9C4FF2 100%)',
            position: 'relative',
          }}
        >
          {/* Subtle Background Geometric Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '250px',
              height: '250px',
              background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
              borderRadius: '50%',
              transform: 'translate(30%, -30%)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: '10%',
              width: '180px',
              height: '180px',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 60%)',
              borderRadius: '50%',
              transform: 'translateY(50%)',
            }}
          />
        </Box>

        {/* Header Profile Content */}
        <Box
          sx={{
            padding: '24px 32px',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'center', md: 'flex-end' },
            justifyContent: 'space-between',
            gap: 3,
            position: 'relative',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'center', md: 'flex-end' },
              gap: 3.5,
              width: '100%',
            }}
          >
            {/* Logo Container Overlapping Cover */}
            <Box
              sx={{
                marginTop: '-70px',
                border: '6px solid #FFF',
                borderRadius: '20px',
                backgroundColor: '#FFF',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                display: 'inline-block',
                zIndex: 2,
                flexShrink: 0,
              }}
            >
              <Avatar
                sx={{
                  width: 96,
                  height: 96,
                  backgroundColor: '#7522D1',
                  color: 'white',
                  fontSize: '2.5rem',
                  fontWeight: 800,
                  borderRadius: '14px',
                }}
              >
                {orgData.companyName.split(' ').map(word => word[0]).join('').slice(0, 2)}
              </Avatar>
            </Box>

            {/* Title / Description */}
            <Box sx={{ textAlign: { xs: 'center', md: 'left' }, width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'center', md: 'flex-end' },
                  width: '100%',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: '#030007',
                      mb: 0.5,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {orgData.companyName}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#6B7280', fontWeight: 500 }}>
                    {orgData.tagline || 'Siera Member Organization'}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  startIcon={<ContactSupport />}
                  onClick={handleContactHR}
                  sx={{
                    backgroundColor: '#7522D1',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#6A1DB0',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 6px 20px rgba(117, 34, 209, 0.25)',
                    },
                    textTransform: 'none',
                    paddingX: 3,
                    paddingY: 1.2,
                    borderRadius: '12px',
                    fontWeight: 700,
                    boxShadow: '0 4px 14px rgba(117, 34, 209, 0.15)',
                    height: 'fit-content',
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  Contact HR Department
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Company Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {companyMetrics.map((metric) => {
          const IconComponent = metric.icon;
          return (
            <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={metric.id}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  borderRadius: '16px',
                  border: '1px solid rgba(117, 34, 209, 0.08)',
                  boxShadow: '0 4px 20px rgba(117, 34, 209, 0.02)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 30px rgba(117, 34, 209, 0.06)',
                    borderColor: 'rgba(117, 34, 209, 0.18)',
                  },
                }}
              >
                <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2.5 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '14px',
                      backgroundColor: '#F5EEFC',
                      color: '#7522D1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <IconComponent sx={{ fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6B7280',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        display: 'block',
                      }}
                    >
                      {metric.label}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#030007',
                        fontWeight: 800,
                        fontSize: '1.05rem',
                        mt: 0.5,
                      }}
                    >
                      {metric.value}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* About Section */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          borderRadius: '16px',
          borderLeft: '6px solid #7522D1',
          border: '1px solid rgba(117, 34, 209, 0.08)',
          borderLeftWidth: '6px',
          boxShadow: '0 8px 32px rgba(117, 34, 209, 0.03)',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: '#030007',
            fontWeight: 800,
            mb: 2,
            letterSpacing: '-0.01em',
          }}
        >
          About the Company
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#4B5563',
            lineHeight: 1.75,
            fontSize: '0.975rem',
          }}
        >
          {orgData.description || 'No description provided for this organization.'}
        </Typography>
      </Paper>

      {/* Address and Contact Section */}
      <Grid container spacing={4}>
        <Grid item size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              height: '100%',
              borderRadius: '16px',
              border: '1px solid rgba(117, 34, 209, 0.08)',
              boxShadow: '0 8px 32px rgba(117, 34, 209, 0.03)',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#030007',
                fontWeight: 800,
                mb: 3,
                letterSpacing: '-0.01em',
              }}
            >
              Official Address
            </Typography>

            {/* Custom Interactive CSS Map Representation */}
            <Box
              sx={{
                height: 200,
                backgroundColor: '#F5EEFC',
                borderRadius: '12px',
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden',
                border: '1px solid rgba(117, 34, 209, 0.12)',
              }}
            >
              {/* Map grid lines using CSS backgrounds */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  opacity: 0.12,
                  backgroundImage: 'radial-gradient(#7522D1 1.5px, transparent 1.5px), radial-gradient(#7522D1 1.5px, #F5EEFC 1.5px)',
                  backgroundSize: '24px 24px',
                  backgroundPosition: '0 0, 12px 12px',
                }}
              />
              {/* Mock Road 1 */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '45%',
                  left: '-10%',
                  width: '120%',
                  height: '10px',
                  backgroundColor: 'rgba(117, 34, 209, 0.08)',
                  transform: 'rotate(-8deg)',
                }}
              />
              {/* Mock Road 2 */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '-10%',
                  left: '60%',
                  width: '10px',
                  height: '120%',
                  backgroundColor: 'rgba(117, 34, 209, 0.08)',
                }}
              />
              {/* Pulsing Pin Indicator */}
              <Box sx={{ zIndex: 1, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '44px',
                      height: '44px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(117, 34, 209, 0.25)',
                      animation: 'pulse 2s infinite ease-in-out',
                      '@keyframes pulse': {
                        '0%': { transform: 'translate(-50%, -50%) scale(0.5)', opacity: 1 },
                        '100%': { transform: 'translate(-50%, -50%) scale(1.6)', opacity: 0 },
                      },
                    }}
                  />
                  <LocationOn sx={{ fontSize: 48, color: '#7522D1', position: 'relative', zIndex: 2 }} />
                </Box>
                <Typography variant="subtitle2" sx={{ color: '#7522D1', fontWeight: 800, mt: 1, letterSpacing: '0.02em' }}>
                  HQ Registered Location
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <LocationOn sx={{ color: '#7522D1', mt: 0.5, fontSize: 22 }} />
              <Typography variant="body1" sx={{ color: '#4B5563', lineHeight: 1.6, fontWeight: 500 }}>
                {orgData.address || 'No official address registered.'}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              height: '100%',
              borderRadius: '16px',
              border: '1px solid rgba(117, 34, 209, 0.08)',
              boxShadow: '0 8px 32px rgba(117, 34, 209, 0.03)',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#030007',
                fontWeight: 800,
                mb: 3,
                letterSpacing: '-0.01em',
              }}
            >
              Contact Information
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {contactInfo.map((contact) => {
                const IconComponent = contact.icon;
                return (
                  <React.Fragment key={contact.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: '12px',
                          backgroundColor: '#F5EEFC',
                          color: '#7522D1',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <IconComponent sx={{ fontSize: 20 }} />
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {contact.label}
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#111827', fontWeight: 600, mt: 0.25 }}>
                          {contact.value}
                        </Typography>
                      </Box>
                    </Box>
                    {contact.isDivider && <Divider sx={{ my: 1, borderColor: 'rgba(117, 34, 209, 0.06)' }} />}
                  </React.Fragment>
                );
              })}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Action Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 5 }}>
        <Button
          variant="contained"
          onClick={handleEditOrganization}
          sx={{
            textTransform: 'none',
            paddingX: 4.5,
            paddingY: 1.5,
            borderRadius: '12px',
            backgroundColor: '#7522D1',
            color: '#FFFFFF',
            fontWeight: 750,
            boxShadow: '0 4px 14px rgba(117, 34, 209, 0.2)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: '#6A1DB0',
              transform: 'translateY(-1px)',
              boxShadow: '0 6px 20px rgba(117, 34, 209, 0.3)',
            },
          }}
        >
          Edit Organization Details
        </Button>
      </Box>
    </Container>
  );
};

export default MyOrganization;